<template>
  <div>
    <!-- <form class="form" @submit.prevent="updateQuery">
      <input
        v-model="inputQuery"
        type="text"
        id="findContact"
        class="form-control form__input"
        placeholder="Search for a contact's name"
      />
      <button class="form__button" type="submit">Search contacts</button>
    </form>

    <p>Search results for "{{ inputQueryCopy }}"</p> -->

    <div class="team-page-hero">
      <h1 class="team-page-title">My Team</h1>
      <p class="team-page-subtitle">People and custom groups for this patient</p>
      <div class="team-page-actions">
        <button type="button" class="btn btn-primary" @click="openAddGroupModal">Add Group</button>
        <button type="button" class="btn btn-outline-secondary" @click="openViewGroupsModal">View Groups</button>
      </div>
    </div>

    <!-- Add Group: name + pick members from this patient's team (same proto-group model as Providers) -->
    <div
      class="modal fade team-group-modal"
      id="searchContactsAddGroupModal"
      tabindex="-1"
      aria-labelledby="searchContactsAddGroupLabel"
      aria-hidden="true"
      ref="addGroupModalRef"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="searchContactsAddGroupLabel" class="modal-title">Add group</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-3">
              Creates a subgroup owned by this patient. Only people
              already on the team below can be added.
            </p>
            <div class="mb-3">
              <label class="form-label" for="newSubgroupNameInput">Group name</label>
              <input
                id="newSubgroupNameInput"
                v-model="newSubgroupName"
                type="text"
                class="form-control"
                maxlength="120"
                placeholder="e.g. Weekend caregivers"
              />
            </div>
            <div class="mb-2 fw-semibold">Add members from team</div>
            <div v-if="!teamMembersPickable.length" class="text-muted small">Load the team list first, or no members with accounts are available to add.</div>
            <ul v-else class="list-group team-pick-list">
              <li
                v-for="m in teamMembersPickable"
                :key="m.member_unique_ID"
                class="list-group-item d-flex align-items-center gap-2"
              >
                <input
                  class="form-check-input mt-0"
                  type="checkbox"
                  :value="m.member_unique_ID"
                  v-model="newSubgroupMemberIds"
                />
                <span>{{ (m.firstname || m.firstName || "") + " " + (m.lastname || m.lastName || "") }}</span>
                <span v-if="m.npinumber" class="text-muted small ms-1">NPI {{ m.npinumber }}</span>
              </li>
            </ul>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" :disabled="addGroupSaving" @click="submitNewPatientSubgroup">
              {{ addGroupSaving ? "Saving…" : "Create group" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- View Groups: patient-owned subgroups from messaging API -->
    <div
      class="modal fade team-group-modal"
      id="searchContactsViewGroupsModal"
      tabindex="-1"
      aria-labelledby="searchContactsViewGroupsLabel"
      aria-hidden="true"
      ref="viewGroupsModalRef"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="searchContactsViewGroupsLabel" class="modal-title">Patient groups</h5>
          </div>
          <div class="modal-body">
            <p v-if="viewGroupsLoading" class="text-muted">Loading…</p>
            <p v-else-if="!patientMessagingGroups.length" class="text-muted">No subgroups yet. Use Add Group to create one.</p>
            <div v-else>
              <div
                v-for="(g, idx) in patientMessagingGroups"
                :key="g.group_id || g.group_name || idx"
                class="card mb-3 patient-subgroup-card"
              >
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 w-100">
                    <div v-if="renamingGroupId === g.group_id" class="d-flex flex-grow-1 align-items-center gap-2 flex-wrap" style="min-width: 12rem">
                      <input
                        v-model="renameGroupName"
                        type="text"
                        class="form-control form-control-sm"
                        maxlength="120"
                        placeholder="Subgroup name"
                        @keyup.enter="saveRenameSubgroup(g)"
                      />
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="renameGroupSaving"
                        @click="saveRenameSubgroup(g)"
                      >
                        {{ renameGroupSaving ? "Saving…" : "Save" }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        :disabled="renameGroupSaving"
                        @click="cancelRenameGroup"
                      >
                        Cancel
                      </button>
                    </div>
                    <span v-else class="fw-semibold">{{ g.group_name || "Unnamed group" }}</span>
                    <div v-if="renamingGroupId !== g.group_id" class="d-flex align-items-center gap-2 ms-auto">
                      <span class="badge bg-secondary">{{ (g.members && g.members.length) || 0 }} members</span>
                      <button
                        v-if="g.group_id"
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        @click="startRenameGroup(g)"
                      >
                        Rename
                      </button>
                    </div>
                  </div>
                </div>
                <ul v-if="g.members && g.members.length" class="list-group list-group-flush">
                  <li
                    v-for="(mem, mi) in g.members"
                    :key="mem.unique_ID || mem.id || mi"
                    class="list-group-item d-flex justify-content-between align-items-start gap-2"
                  >
                    <div class="d-flex flex-column gap-1">
                      <span><strong>{{ mem.name }}</strong></span>
                      <span v-if="mem.email" class="text-muted small">{{ mem.email }}</span>
                      <span
                        v-if="mem.accountType"
                        class="badge bg-light text-dark text-capitalize align-self-start"
                        >{{ mem.accountType }}</span
                      >
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger flex-shrink-0"
                      :disabled="isRemovingMember(g.group_id, mem)"
                      @click="removeMemberFromSubgroup(g, mem)"
                    >
                      {{ isRemovingMember(g.group_id, mem) ? "Removing…" : "Remove" }}
                    </button>
                  </li>
                </ul>
                <div v-else class="card-body text-muted small">No members listed for this group.</div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <div class="team-cards-module">
      <TeamSearchBar />
      <div class="cards-container">
        <TransitionGroup name="card-list" tag="div" class="cards-grid">
          <TeamCard
            v-for="member in teamStore.filteredProviders"
            :key="member.id"
            :patprovider="member"
            @email="openEmailModal"
            @sms="openSmsModal"
            @call="openCallModal"
          />
        </TransitionGroup>
        <div
          v-if="teamStore.filteredProviders.length === 0 && allProvs.length === 0 && allNurses.length === 0"
          class="empty-state"
        >
          <span class="glyphicon glyphicon-search"></span>
          <h3>No Team Members Found</h3>
          <p>Try adjusting your search or filter criteria, or add a group to get started.</p>
        </div>
      </div>

      <!-- Legacy fallback rendering, kept for the rare case where the team flatten
           returns nothing but these directory calls still have data. -->
      <ul v-if="teamStore.filteredProviders.length === 0 && allProvs.length != 0" class="result-list">
        <li v-for="(provider, index) in allProvs" :key="index">
          <div class="result-item">
            <h2 class="header3 capitalize">
              <span v-if="provider.firstname">{{ provider.firstname.toLowerCase() + " " }}</span>
              <span v-if="provider.lastname">{{ provider.lastname.toLowerCase() }}</span>
            </h2>
            <current-provider-bio :provider="provider"></current-provider-bio>
          </div>
        </li>
      </ul>
      <ul v-if="teamStore.filteredProviders.length === 0 && allNurses.length != 0" class="result-list">
        <li v-for="(nurse, index) in allNurses" :key="index">
          <div class="result-item">
            <h2 class="header3 capitalize">
              <span>{{ nurse.firstname.toLowerCase() }} </span>
              <span>{{ nurse.lastname.toLowerCase() }}</span>
            </h2>
            <current-provider-bio :provider="nurse"></current-provider-bio>
          </div>
        </li>
      </ul>

      <CallComposeModal
        :show="callModalOpen"
        :patprovider="selectedTeamMember"
        :provider="teamStore.currentProvider"
        @close="callModalOpen = false"
        @callsent="handleCallSent"
      />
      <EmailComposeModal
        :show="emailModalOpen"
        :patprovider="selectedTeamMember"
        :provider="teamStore.currentProvider"
        @close="emailModalOpen = false"
        @sent="handleEmailSent"
      />
      <SmsComposeModal
        :show="smsModalOpen"
        :patprovider="selectedTeamMember"
        :provider="teamStore.currentProvider"
        @close="smsModalOpen = false"
        @sent="handleSmsSent"
      />

      <Transition name="toast">
        <div v-if="toastMessage" class="toast" :class="toastType">
          {{ toastMessage }}
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { CURR_API, OTHER_API } from "@/constants/apis";
import SearchContactsResults from "./SearchContactsResults";
import CurrentProviderBio from "../providers/CurrentProviderBio";
import ContactBio from "./ContactBio";
import { mapGetters } from "vuex";
import { getContacts } from "@/api/userApi";
import $ from "jquery";
import axios from "axios";
import { Modal } from "bootstrap";
import { useTeamCardsStore } from "@/stores/teamCardsStore";
import TeamSearchBar from "./TeamSearchBar.vue";
import TeamCard from "./TeamCard.vue";
import CallComposeModal from "./CallComposeModal.vue";
import EmailComposeModal from "./EmailComposeModal.vue";
import SmsComposeModal from "./SmsComposeModal.vue";
export default {
  name: "SearchContacts",
  components: {
    "search-contacts-results": SearchContactsResults,
    "current-provider-bio": CurrentProviderBio,
    "contact-bio": ContactBio,
    TeamSearchBar,
    TeamCard,
    CallComposeModal,
    EmailComposeModal,
    SmsComposeModal,
  },
  data() {
    return {
      allContacts: [],
      uContacts: [],
      allProvs: [],
      allNurses: [],
      newGroupEmails: [],
      newGroupProvs: [],
      newGroupNurses: [],
      grouplist: [],
      groupNumber: [],
      inputQuery: "",
      inputQueryCopy: "", // required. can't use `inputQuery` in the else block because it is reactive',
      keywords: [],
      patientMessagingGroups: [],
      newSubgroupName: "",
      newSubgroupMemberIds: [],
      addGroupSaving: false,
      viewGroupsLoading: false,
      renamingGroupId: null,
      renameGroupName: "",
      renameGroupSaving: false,
      removingMemberKey: null,
      selectedTeamMember: null,
      emailModalOpen: false,
      smsModalOpen: false,
      callModalOpen: false,
      toastMessage: "",
      toastType: "success",
    };
  },
  beforeMount() {
    // "My team" (WelcomePage) routes here: /search?category=contact&query=
    // This page should show the SAME people as internal messaging recipients for this patient
    // (i.e., subgroup members across healthcare + family).
    this.inputQuery = this.$route.query.query;
    this.teamStore.loadMyTeam();
  },
  async mounted() {
    console.log("=== SearchContacts mounted ===");
    console.log("this.allData on mount:", this.allData);
    console.log("this.allData?.patient on mount:", this.allData?.patient);
    console.log("this.allData?.manager on mount:", this.allData?.manager);

    // this.addProviders();
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    teamStore() {
      return useTeamCardsStore();
    },
    patient: function () {
      console.log("=== patient computed property ===");
      console.log("this.allData:", this.allData);
      console.log("this.allData?.patient:", this.allData?.patient);
      const result = this.allData?.patient || null;
      console.log("patient computed result:", result);
      return result;
    },
    manager: function () {
      console.log("=== manager computed property ===");
      console.log("this.allData:", this.allData);
      console.log("this.allData?.manager:", this.allData?.manager);
      const result = this.allData?.manager || null;
      console.log("manager computed result:", result);
      return result;
    },
    relationships: function () {
      return this.allData.relationDescriptions;
    },
    teamMembersPickable() {
      const pid = String(this.patient?.externId || "").trim();
      return (this.teamStore.rawTeam || []).filter((c) => {
        const uid = String(c.member_unique_ID || "").trim();
        return uid && uid !== pid;
      });
    },
    actorUniqueId() {
      return (
        (this.allData && this.allData.manager && this.allData.manager.externid) ||
        (this.allData && this.allData.patient && this.allData.patient.externId) ||
        ""
      );
    },
  },
  methods: {
    normalizeApiPayload(data) {
      if (!data || typeof data !== "object") return {};
      if (Object.prototype.hasOwnProperty.call(data, "status") || Object.prototype.hasOwnProperty.call(data, "data")) {
        return data;
      }
      const keys = Object.keys(data);
      if (keys.length === 1 && data[keys[0]] && typeof data[keys[0]] === "object") {
        return data[keys[0]];
      }
      return data;
    },

    openEmailModal(member) {
      this.selectedTeamMember = member;
      this.emailModalOpen = true;
    },
    openSmsModal(member) {
      this.selectedTeamMember = member;
      this.smsModalOpen = true;
    },
    openCallModal(member) {
      this.selectedTeamMember = member;
      this.callModalOpen = true;
    },
    showToast(message, type = "success") {
      this.toastMessage = message;
      this.toastType = type;
      setTimeout(() => {
        this.toastMessage = "";
      }, 3000);
    },
    handleEmailSent(data) {
      this.showToast(`Email sent to ${data?.providerName || "team member"}`);
    },
    handleSmsSent(data) {
      this.showToast(`SMS sent to ${data?.to || "team member"}`);
    },
    handleCallSent(data) {
      this.showToast(`Call sent to ${data?.to || "team member"}`);
    },

    async loadNurses() {
      console.log("=== loadNurses Debug ===");
      console.log("this.patient:", this.patient);
      console.log("this.patient?.externId:", this.patient?.externId);
      console.log("this.patient?.externId2:", this.patient?.externId2);
      
      // Check if patient data is available
      if (!this.patient?.externId || !this.patient?.externId2) {
        console.error("Patient data not available");
        console.log("Patient check failed - patient:", this.patient);
        return;
      }
      
      let content = {
        NursyInfo: {
          API_Meth: "GetPatientNurse",
          accountnumber: this.patient.externId2,
          patientnumber: this.patient.externId,
        },
      };
      await axios
        .post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        })
        .then((res) => {
          if (res.data) {
            this.allNurses = res.data;
            console.log(this.allNurses);
          }
        });
    },
    async loadProviders() {
      console.log("=== loadProviders Debug ===");
      console.log("this.allData:", this.allData);
      console.log("this.allData?.patient:", this.allData?.patient);
      console.log("this.allData?.patient?.externId:", this.allData?.patient?.externId);
      
      // Check if patient data is available
      if (!this.allData?.patient?.externId) {
        console.error("Patient data not available");
        console.log("Patient check failed - allData:", this.allData);
        return;
      }
      
      let content = {
        Provider: {
          API_Meth: "GetAllProviders",
          patientid: this.allData.patient.externId,
        },
      };
      await axios
        .post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        })
        .then((res) => {
          // console.log(res);
          if (res.data.provider) {
            this.allProvs = res.data.provider.filter((prov) => prov.onTeam == "1");
          } else {
            this.allProvs = [];
          }
        });
    },
    openAddGroupModal() {
      this.newSubgroupName = "";
      this.newSubgroupMemberIds = [];
      const el = this.$refs.addGroupModalRef;
      if (el) {
        Modal.getOrCreateInstance(el).show();
      }
    },
    memberRemoveKey(groupId, member) {
      const uid = String(member?.unique_ID || member?.id || "").trim();
      return `${groupId}:${uid}`;
    },
    isRemovingMember(groupId, member) {
      return this.removingMemberKey === this.memberRemoveKey(groupId, member);
    },
    async openViewGroupsModal() {
      this.cancelRenameGroup();
      this.removingMemberKey = null;
      const el = this.$refs.viewGroupsModalRef;
      if (el) {
        Modal.getOrCreateInstance(el).show();
      }
      this.viewGroupsLoading = true;
      await this.refreshPatientMessagingGroups();
      this.viewGroupsLoading = false;
    },
    startRenameGroup(group) {
      this.renamingGroupId = group.group_id;
      this.renameGroupName = group.group_name || "";
    },
    cancelRenameGroup() {
      this.renamingGroupId = null;
      this.renameGroupName = "";
    },
    async saveRenameSubgroup(group) {
      // Ensure name is not empty
      const name = (this.renameGroupName || "").trim();
      if (!name) {
        alert("Please enter a group name.");
        return;
      }
      // Ensure name is unique
      const duplicate = this.patientMessagingGroups.some(
        (g) =>
          g.group_id !== group.group_id &&
          String(g.group_name || "")
            .trim()
            .toLowerCase() === name.toLowerCase()
      );
      if (duplicate) {
        alert("A subgroup with this name already exists for this patient.");
        return;
      }
      const accountNumber = this.patient?.externId2;
      this.renameGroupSaving = true;
      try {
        // Send out the request to rename the group
        const content = {
          Groups: {
            API_Meth: "UpdateGroup",
            accountnumber: accountNumber,
            group_id: group.group_id,
            name,
            parent_group_id: null,
            actor_unique_ID: this.actorUniqueId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const ok = payload && String(payload.status || "").toLowerCase().includes("success");
        if (ok) {
          this.cancelRenameGroup();
          await this.refreshPatientMessagingGroups();
        } else {
          alert(payload?.message || "Could not rename group.");
        }
      } catch (e) {
        console.error(e);
        alert("Could not rename group.");
      } finally {
        this.renameGroupSaving = false;
      }
    },
    async removeMemberFromSubgroup(group, member) {
      const uniqueId = String(member?.unique_ID || member?.id || "").trim();
      if (!uniqueId || !group?.group_id) return;
      const accountNumber = this.patient?.externId2;
      const memberLabel = member?.name || uniqueId;
      const groupLabel = group?.group_name || "this group";
      if (!confirm(`Remove ${memberLabel} from ${groupLabel}?`)) {
        return;
      }
      const removeKey = this.memberRemoveKey(group.group_id, member);
      this.removingMemberKey = removeKey;
      try {
        const content = {
          Groups: {
            API_Meth: "RemoveUserFromGroup",
            accountnumber: accountNumber,
            unique_ID: uniqueId,
            group_id: group.group_id,
            actor_unique_ID: this.actorUniqueId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const ok = payload && String(payload.status || "").toLowerCase().includes("success");
        if (ok) {
          await this.refreshPatientMessagingGroups();
          await this.teamStore.loadMyTeam();
        } else {
          alert(payload?.message || "Could not remove member from group.");
        }
      } catch (e) {
        console.error(e);
        alert("Could not remove member from group.");
      } finally {
        if (this.removingMemberKey === removeKey) {
          this.removingMemberKey = null;
        }
      }
    },
    async refreshPatientMessagingGroups() {
      const patientId = this.patient?.externId;
      const accountNumber = this.patient?.externId2;
      if (!patientId || !accountNumber) {
        this.patientMessagingGroups = [];
        return;
      }
      try {
        const content = {
          Messages: {
            API_Meth: "GetPatientTeamForMessaging",
            accountnumber: accountNumber,
            patient_unique_ID: patientId,
          },
        };
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(response.data);
        const groups =
          payload && String(payload.status || "").toLowerCase().includes("success")
            ? Array.isArray(payload.data)
              ? payload.data
              : []
            : [];
        this.patientMessagingGroups = groups;
      } catch (e) {
        console.error(e);
        this.patientMessagingGroups = [];
      }
    },
    async submitNewPatientSubgroup() {
      const name = (this.newSubgroupName || "").trim();
      if (!name) {
        alert("Please enter a group name.");
        return;
      }
      const accountNumber = this.patient?.externId2;
      const patientId = String(this.patient?.externId || "").trim();
      if (!accountNumber || !patientId) {
        alert("Patient account is not loaded.");
        return;
      }
      this.addGroupSaving = true;
      try {
        const body = {
          Messages: {
            API_Meth: "CreatePatientOwnedSubgroupWithMembers",
            accountnumber: accountNumber,
            patient_unique_ID: patientId,
            group_name: name,
            member_unique_IDs: Array.isArray(this.newSubgroupMemberIds)
              ? [...this.newSubgroupMemberIds]
              : [],
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", body, {
          headers: { "Content-Type": "application/json;" },
        });
        const data = res.data;
        const ok = data && String(data.status || "").toLowerCase().includes("success");
        if (ok) {
          const modalEl = this.$refs.addGroupModalRef;
          if (modalEl) {
            Modal.getOrCreateInstance(modalEl).hide();
          }
          await this.teamStore.loadMyTeam();
          alert("Group created.");
        } else {
          alert(data?.message || "Could not create group.");
        }
      } catch (e) {
        console.error(e);
        alert("Could not create group.");
      } finally {
        this.addGroupSaving = false;
      }
    },
    searchContact() {
      // Legacy search: keep behavior for non-empty queries.
      // For "My team" (query is empty), we want the full team list from `loadMyTeam()`.
      const q = (this.inputQuery || "").trim();
      if (!q) {
        this.inputQueryCopy = "";
        this.keywords = [];
        return;
      }

      this.uContacts = [];
      this.inputQueryCopy = q;

      // break down user input to an array of strings, split by white spaces
      this.keywords = stringToArray(q);
      let regexp = new RegExp(this.keywords.join("|"), "i");

      this.allContacts.forEach((c) => {
        if (
          (c.firstName && regexp.test(c.firstName)) ||
          (c.lastName && regexp.test(c.lastName))
        ) {
          this.uContacts.push(c);
        }
      });
    },

    updateQuery() {
      // not redirect, just replace the string
      this.$router.replace({
        path: "search",
        query: { category: "contact", query: this.inputQuery },
      });
      this.searchContact();
    },
  },
};

let stringToArray = (string) => {
  if (!string || typeof string !== 'string') {
    return [];
  }
  return string.split(/(\s+)/).filter((e) => {
    return e.trim().length > 0;
  });
};
</script>

<style scoped lang="scss">
.team-page-hero {
  text-align: center;
  padding: 1.25rem 1rem 1.75rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.team-page-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.team-page-subtitle {
  color: #6c757d;
  font-size: 0.95rem;
  margin-bottom: 1rem;
}

.team-page-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
}

.team-pick-list {
  max-height: 320px;
  overflow-y: auto;
}

.patient-subgroup-card {
  text-align: left;
}

.team-cards-module {
  background: var(--emr-bg);
  --emr-primary: #1a73e8;
  --emr-primary-dark: #1557b0;
  --emr-primary-light: #e8f0fe;
  --emr-bg: #f5f7fa;
  --emr-surface: #ffffff;
  --emr-text: #1f2937;
  --emr-text-secondary: #6b7280;
  --emr-border: #e2e8f0;
  --emr-success: #16a34a;
  --emr-warning: #f59e0b;
  --emr-danger: #dc2626;
  --emr-radius: 6px;
  --emr-card: #ffffff;
  --topbar-height: 56px;
}

.cards-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 24px;
  width: 100%;
}

.cards-grid {
  display: grid;
  /* Slightly narrower min so ~3–4 cards fit in the widened page. */
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 24px;
  color: var(--emr-text-secondary);
}

.empty-state .glyphicon {
  font-size: 48px;
  color: var(--emr-border);
  display: block;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--emr-text);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  margin: 0 0 20px 0;
}

/* Card list transitions */
.card-list-enter-active,
.card-list-leave-active {
  transition: all 0.4s ease;
}

.card-list-enter-from,
.card-list-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.card-list-move {
  transition: transform 0.4s ease;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 14px 20px;
  border-radius: var(--emr-radius);
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 2000;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.toast.success {
  background: #10b981;
  color: white;
}

.toast.info {
  background: var(--emr-primary);
  color: white;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@media (max-width: 768px) {
  .cards-container {
    padding: 16px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .toast {
    left: 16px;
    right: 16px;
    bottom: 16px;
  }

  /*
   * Mobile sticky nav (z-index 2000500) sits above Bootstrap modals and clips
   * the dialog header. Inset top/bottom so title + footer actions stay usable
   * above the Messages bar.
   */
  .team-group-modal {
    z-index: 2000600;
  }

  .team-group-modal .modal-dialog {
    margin: 5.25rem 0.75rem 4.75rem;
    max-height: calc(100dvh - 5.25rem - 4.75rem);
  }

  .team-group-modal .modal-content {
    max-height: calc(100dvh - 5.25rem - 4.75rem);
  }

  .team-pick-list {
    max-height: min(240px, 35vh);
  }
}

@media (max-width: 480px) {
  .cards-container {
    padding: 12px;
  }
}

@import "../../../stylesheets/mixins/flex";

$color-base: #0060ff;
$color-font: #58595b;

ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

button {
  border: none;
  cursor: pointer;
  background-color: #2196f3;
  color: #fff;
  padding: 5px 10px;
  font-size: 14px;
}

button:hover {
  background-color: #0060ff;
}

h2 {
  font-size: 1.5em;
  color: $color-font;
  margin-top: 2em;
  text-align: center;
}

p {
  margin-top: 1em;
  font-size: 0.85em;
}

.list {
  margin: 0 auto;
}

.form-control {
  font-size: 0.85em;
}

.form {
  @include flex-box();
  @include flex-wrap(wrap);

  .form__input {
    @include flex(1, 0, 70%);
  }

  .form__button {
    @include flex(1, 0, 20%);
  }
}

header2 {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 20px;
  color: #0060ff;
}

.title {
  margin-right: 20px;
}

.result-list {
  width: 700px;
}

.result-item {
  border: 1px solid lightgray;
  border-radius: 5px;
  margin: 2em auto 0;
  text-align: left;
}

.result-item:first-child {
  margin-top: 1em;
}

.header3 {
  text-align: center;
  color: #fff;
  font-size: 1.2em;
  padding: 5px 0;
  background-color: #007bff;
}

.capitalize {
  text-transform: capitalize;
}

.result-item-link {
  display: block;
  text-align: right;
  font-size: 0.85em;
}

ul {
  list-style-type: none;
  margin: 0 auto;
}

.providerGroup-listcard {
  display: flex;
  justify-content: space-evenly;
  border-style: dashed;
  border-width: 1px;
  width: 100%;
}

/*Keyon Add Provider CSS*/
.provcardSec {
  display: none;
  align-items: center;
  position: relative;
  flex-direction: column;
}

.emailcanvas {
  display: none;
}

#cllprov {
  margin-left: 20px;
}

#providetomail {
  margin-left: 30px;
}

#provemailsubject {
  margin-left: 30px;
}

#provid-emailmsg {
  margin-left: 30px;
  margin-top: -10px;
  margin-bottom: 10px;
  width: 400px;
  height: 100px;
}

#sendprovid-emailbtn {
  margin-left: 30px;
}

.emailcanvas .container {
  box-shadow: 1px 1px 9px #5f5852;
  position: absolute;
  background-color: #ffffff;
  width: 855px;
  height: auto; // 710px;
  padding-bottom: 20px;
  margin: 0 auto;
}

.emailcanvas .btn-container {
  padding-bottom: 30px;
}

.emailcanvas label {
  margin-bottom: 20px;
}

#providetomail {
  margin-left: 30px;
}

.emailcanvas ul {
  display: flex;
  margin-top: -64px;
}

.emailcanvas .emailli {
  list-style: none;
  margin-left: 20px;
}

.emailcanvas .emailblock {
  background-color: #aaabac;
  padding: 5px 5px;
  width: auto;
  height: auto;
  margin-left: 12px;
  position: relative;
  top: 17px;
}

.emailcanvas .xout {
  background-color: white;
  cursor: pointer;
}

.mmailbox {
  background-color: #55555512;
  width: 100%;
  position: relative;
  z-index: 3000000000;
  top: -35px;
}

.mmailbox #hiddenmail {
  display: none;
}

.emailcanvas .selectbox {
  margin-top: -10px;
}

.inputvalholder {
  display: none;
  justify-content: flex-start;
}

.listGroupSec {
  display: none;
}

.listGroupSec .card .accordhead {
  display: flex;
  flex-direction: row-reverse;
  border-style: solid;
  border-width: 1px;
  border-color: #606565b0;
}

.listGroupSec .card .caret {
  text-align: right;
  width: 100px;
}

.listGroupSec .card .grp-name {
  width: 100%;
  margin: 0 auto;
}

.listGroupSec .card .grp-name p {
  text-align: center;
}

.listGroupSec .accordionbottom {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  border-style: solid;
  border-width: 1px;
  border-color: #0433;
  display: flex;
}

.listGroupSec .providminicard {
  display: flex;
  flex-direction: row-reverse;
  border-bottom-style: solid;
  border-bottom-width: 1px;
  flex-direction: row-reverse;
  padding: 11px;
  margin-bottom: 3px;
}

.listGroupSec #accord-caret {
  cursor: pointer;
}

.listGroupSec #accord-caret:hover {
  color: #4553;
}

.listGroupSec .groupcanvas .card {
  display: flex;
  flex-direction: column;
}

.listGroupSec .subnavcontainer {
  display: none;
  flex-direction: column;
  width: 248px;
  margin-left: -153px;
  border-style: ridge;
  padding: 11px;
}

.listGroupSec #elipse {
  cursor: pointer;
}

.listGroupSec .closecanvas {
  display: none;
}

.listGroupSec .creategrpcanvas {
  display: none;
}

.listGroupSec .groupcanvas {
  display: none;
}

.accordionbottom {
  display: none;
}

.smsconferencecanvas {
  display: none;
}

.listGroupSec .accordionbottom {
  display: none;
}

.groupemailcecanvas {
  display: none;
}

#sbmttxtconfbtn {
  margin-top: 20px;
  margin-bottom: 20px;
  margin-right: 20px;
}

#smsconferencebox {
  width: 650px;
  height: 220px;
}

#smsconferencephone {
  width: 425px;
  height: 45px;
  margin-bottom: 20px;
  margin-top: 20px;
  margin-left: 20px;
}

.smsconferencecanvas .container {
  padding: 10px;
  display: flex;
  text-align: center;
}

.groupemailcecanvas .container {
  padding: 10px 100px;
  display: flex;
  text-align: center;
}

li h2 {
  margin-top: 2%;
}

#emailform {
  border-style: solid;
  border-width: 1px;
  border-color: #003330;
}

#conferenceEmail {
  width: 425px;
  height: 45px;
  margin-bottom: 20px;
  margin-top: 20px;
  margin-left: 20px;
}

#emailconferencebox {
  width: 650px;
  height: 220px;
}

#sbmtemailconfbtn {
  margin-top: 15px;
  margin-bottom: 15px;
}

.smsconferencecanvas #closeconfcallbtn {
  margin-left: 15px;
}

#groupemailcancelbtn {
  margin-left: 15px;
}

.prvtxtbox {
  height: 42px;
}

/* Mobile-specific z-index adjustments for mobile message widget */
@media (max-width: 768px) {
  .mmailbox {
    z-index: 1 !important;
  }
  
  /* Ensure all high z-index elements stay below mobile widget */
  [style*="z-index"] {
    z-index: 1 !important;
  }
}
</style>
